import type { LucideIcon } from 'lucide-react';
import { UploadIcon } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { NavLink } from 'react-router';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';

/*
 * The app sidebar's rail-facing parts: every control that shows in the folded
 * icon rail, and the footer that holds the user menu. AppSidebar supplies the
 * data; these own the geometry, so the frontend-only layout suite can hold the
 * rail to it with the same markup the app renders.
 */

export interface NavItem {
  readonly href: string;
  readonly icon: LucideIcon;
  readonly label: string;
  readonly activeHrefs?: readonly string[];
  readonly match?: 'exact' | 'none' | 'prefix';
  /**
   * Folio how-to target id (data-folio-target). Mirrors `RegisteredPage`
   * entryPath testIds so the lookup tool can point Sean at the live affordance.
   * See `apps/web/src/folio/page-registry.ts`.
   */
  readonly folioTargetId?: string;
  /**
   * Set while this capability lane has not been announced in this browser
   * (docs/design/ia-shape-brief.md §7). The lane plays the one-shot pop-in and
   * accent flash on mount and calls this when the flash ends, so the caller can
   * record the announcement and later mounts render the lane like its peers.
   */
  readonly onPopInEnd?: () => void;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function NavItems({
  items,
  isActive,
}: {
  readonly items: readonly NavItem[];
  readonly isActive: (item: NavItem) => boolean;
}) {
  return (
    <SidebarMenu>
      {items.map((item) => {
        const active = isActive(item);
        const { onPopInEnd } = item;
        return (
          <SidebarMenuItem
            key={`${item.label}-${item.href}`}
            className={onPopInEnd === undefined ? undefined : 'ca-capability-pop-in'}
            onAnimationEnd={
              onPopInEnd === undefined
                ? undefined
                : (event) => {
                    if (
                      event.target === event.currentTarget &&
                      event.animationName === 'ca-capability-flash'
                    ) {
                      onPopInEnd();
                    }
                  }
            }
          >
            {active ? (
              <span
                className="pointer-events-none absolute top-2 bottom-2 left-0 w-0.5 rounded-full bg-sidebar-primary"
                aria-hidden="true"
              />
            ) : null}
            <SidebarMenuButton
              asChild
              isActive={active}
              tooltip={item.label}
              className={cn(
                'h-auto min-h-9 group-data-[collapsible=icon]:size-11! group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:p-0! group-data-[collapsible=icon]:[&>span:last-child]:hidden',
                active &&
                  'bg-sidebar-primary/8 text-sidebar-foreground font-medium data-active:bg-sidebar-primary/8 data-active:text-sidebar-foreground',
              )}
            >
              <NavLink
                aria-label={item.label}
                to={item.href}
                {...(item.folioTargetId === undefined
                  ? {}
                  : { 'data-folio-target': item.folioTargetId })}
              >
                <item.icon />
                <span>{item.label}</span>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );
}

export function SidebarNavSection({
  ariaLabel,
  items,
  isActive,
  label,
}: {
  readonly ariaLabel: string;
  readonly items: readonly NavItem[];
  readonly isActive: (item: NavItem) => boolean;
  readonly label: string;
}) {
  if (items.length === 0) {
    return null;
  }

  return (
    <SidebarGroup>
      <SidebarGroupLabel className="sr-only">{label}</SidebarGroupLabel>
      <SidebarGroupContent>
        <nav aria-label={ariaLabel}>
          <NavItems items={items} isActive={isActive} />
        </nav>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

/** A project's primary action; folded, it is an icon button the size of the rail's nav buttons. */
export function SidebarUploadAction({ href }: { readonly href: string }) {
  return (
    <SidebarGroup>
      <SidebarGroupLabel className="sr-only">Project actions</SidebarGroupLabel>
      <SidebarGroupContent>
        <div className="px-2 group-data-[collapsible=icon]:px-0">
          <Button
            asChild
            variant="default"
            size="lg"
            className="w-full justify-start group-data-[collapsible=icon]:size-11! group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0!"
          >
            <NavLink to={href} title="Upload documents">
              <UploadIcon data-icon="inline-start" />
              <span className="group-data-[collapsible=icon]:sr-only">Upload documents</span>
            </NavLink>
          </Button>
        </div>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

/** The user menu's trigger: avatar and name, folding to the avatar alone. */
export function SidebarUserMenuButton({
  displayName,
  photoUrl,
  role,
  ...props
}: Omit<ComponentProps<typeof SidebarMenuButton>, 'children' | 'size'> & {
  readonly displayName: string;
  readonly photoUrl: string | null;
  readonly role: string;
}) {
  return (
    <SidebarMenuButton
      {...props}
      className="group-data-[collapsible=icon]:size-11! group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:p-0!"
      size="lg"
      aria-label={`User menu for ${displayName}`}
      data-folio-target="sidebar-user-menu-trigger"
    >
      <Avatar className="size-8">
        {photoUrl !== null ? (
          <AvatarImage alt={displayName} src={photoUrl} />
        ) : null}
        <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
      </Avatar>
      <div className="flex flex-col gap-0.5 leading-none group-data-[collapsible=icon]:hidden">
        <span className="text-sm font-medium">{displayName}</span>
        <span className="text-xs text-muted-foreground">{role}</span>
      </div>
    </SidebarMenuButton>
  );
}

/**
 * The sidebar footer: usage, then the agent connection entry directly above the
 * user menu, then the copyright line.
 */
export function AppSidebarFooter({
  usage,
  agentConnection,
  userMenu,
}: {
  readonly usage: ReactNode;
  readonly agentConnection: ReactNode;
  readonly userMenu: ReactNode;
}) {
  return (
    <SidebarFooter className="overflow-x-hidden">
      {usage}
      {agentConnection}
      <nav aria-label="User menu">
        <SidebarMenu>
          <SidebarMenuItem>{userMenu}</SidebarMenuItem>
        </SidebarMenu>
      </nav>
      <p className="px-2 text-sm leading-snug text-muted-foreground group-data-[collapsible=icon]:hidden">
        © 2026 EYGM Limited. All rights reserved.
      </p>
    </SidebarFooter>
  );
}
